import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        const response = await API.get(`/verify-email?token=${token}`);
        setStatus('success');
        setMessage(response.data.msg || 'Email verified successfully!');
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.detail || 'Verification failed. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      style={{ 
        minHeight: '100vh', 
        background: '#111111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}
    >
      {/* Animated Background Particles */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}>
        {[...Array(10)].map((_, i) => (
          <motion.div
            key={i}
            style={{
              position: 'absolute',
              width: '2px',
              height: '2px',
              background: '#7CFC00',
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: 0.6,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <motion.div 
        style={{ 
          maxWidth: '500px', 
          width: '100%',
          padding: '3rem', 
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.02)',
          borderRadius: '20px',
          border: '1px solid rgba(124, 252, 0, 0.2)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 0 30px rgba(124, 252, 0, 0.1)',
          position: 'relative',
          zIndex: 10
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
      >
        {status === 'verifying' && (
          <>
            <motion.div 
              style={{ fontSize: '4rem', marginBottom: '1rem' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4, type: 'spring' }}
            >
              ⏳
            </motion.div>
            
            <motion.h2 
              style={{ fontSize: '2rem', marginBottom: '1rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              Verifying Your Email...
            </motion.h2>
            
            <motion.div
              style={{ width: '50px', height: '50px', border: '3px solid #7CFC00', borderTop: '3px solid transparent', borderRadius: '50%', margin: '0 auto 1rem' }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div 
              style={{ fontSize: '4rem', marginBottom: '1rem' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4, type: 'spring' }}
            >
              ✅
            </motion.div>
            
            <motion.h2 
              style={{ fontSize: '2rem', marginBottom: '1rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              Email Verified!
            </motion.h2>
            
            <motion.p 
              style={{ fontSize: '1.1rem', color: '#e0e0e0', lineHeight: '1.6', marginBottom: '2rem' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {message}
            </motion.p>
            
            <motion.button
              onClick={() => navigate('/login')}
              style={{
                backgroundColor: '#7CFC00',
                color: '#000',
                padding: '0.8rem 2rem',
                borderRadius: '8px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' }}
              whileTap={{ scale: 0.98 }}
            >
              Go to Login
            </motion.button>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div 
              style={{ fontSize: '4rem', marginBottom: '1rem' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4, type: 'spring' }}
            >
              ❌
            </motion.div>
            
            <motion.h2 
              style={{ fontSize: '2rem', marginBottom: '1rem', fontFamily: 'Poppins, sans-serif', fontWeight: '600', color: 'white' }}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              Verification Failed
            </motion.h2>
            
            <motion.p 
              style={{ fontSize: '1.1rem', color: '#e0e0e0', lineHeight: '1.6', marginBottom: '2rem' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {message}
            </motion.p>
            
            <motion.div
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.0 }}
            >
              <motion.button
                onClick={() => navigate('/register')}
                style={{
                  backgroundColor: '#7CFC00',
                  color: '#000',
                  padding: '0.8rem 2rem',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
                whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(124, 252, 0, 0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                Register Again
              </motion.button>
              
              <motion.button
                onClick={() => navigate('/')}
                style={{
                  backgroundColor: 'transparent',
                  color: '#7CFC00',
                  padding: '0.6rem 1.5rem',
                  borderRadius: '8px',
                  border: '1px solid #7CFC00',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
                whileHover={{ scale: 1.02, boxShadow: '0 0 15px rgba(124, 252, 0, 0.3)' }}
                whileTap={{ scale: 0.98 }}
              >
                Back to Home
              </motion.button>
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
} 